/**
 * Picker permission for a form (ORCH-366)
 *
 * Purpose: The permission a form's pickers are scoped to, from the model the form edits
 *          and its mode, so every form offers only values a save will accept without
 *          its author writing a <PickerScope>.
 * Logic: Reads the generated MODEL_ACCESS (constants/permissionKeys.ts, from the authz
 *        catalog). A fielddef model
 *        (`fd_*`) uses the master-data keys. A read-only form offers nothing, so it
 *        resolves to no permission. An undeclared model resolves to no permission too;
 *        a backend test fails until the model is declared or listed unscoped.
 */

import {
  FIELDDEF_MODEL_ACCESS,
  MODEL_ACCESS,
  PermissionKey,
  UNSCOPED_MODELS,
} from '../constants/permissionKeys';

export type PickerMode = 'create' | 'edit' | 'view';

const FIELDDEF_PREFIX = 'fd_';
const reported = new Set<string>();

export const resolvePickerPermission = (
  modelId: string,
  mode: PickerMode
): PermissionKey | null => {
  if (mode === 'view') return null; // disabled inputs offer nothing
  const access =
    MODEL_ACCESS[modelId] ?? (modelId.startsWith(FIELDDEF_PREFIX) ? FIELDDEF_MODEL_ACCESS : null);
  if (!access) {
    if (modelId && !UNSCOPED_MODELS.includes(modelId) && !reported.has(modelId)) {
      reported.add(modelId);
      console.error(
        `[pickerPermission] Model '${modelId}' declares no form permissions; its pickers are ` +
          'unscoped. Add it to MODEL_ACCESS in shared_core/authz/catalog.py.'
      );
    }
    return null;
  }
  return mode === 'create' ? access.create : access.edit;
};
